import { useState, useEffect, Fragment } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ClipboardList } from 'lucide-react'
import api from '../services/api'
import { getToken } from '../utils/userData'
import {
  SECTIONS,
  FORM_TITLE,
  formatSession12SectionValue,
  OTHERS_OPTION,
} from '../config/session12DailyRitualsForm'

const FORM_FIELD_LABELS = {
  primaryHealthGoal: 'Primary Health Goal',
  dietaryPreferences: 'Dietary Preferences',
  outsideFoodConsumption: 'Outside Food Consumption',
  dailyEnergyLevels: 'Daily Energy Levels',
  physicalActivity: 'Physical Activity',
  healthCondition: 'Health Condition',
  mealTimingConsistency: 'Meal Timing Consistency',
  waterIntake: 'Water Intake',
  queries: 'Queries',
}

const formatFormValue = (value, key, data) => {
  if (Array.isArray(value)) {
    const parts = value.map((v) => {
      if (v === 'Others' && data?.[`${key}_othersText`]) {
        return `Others (${data[`${key}_othersText`]})`
      }
      return v
    })
    return parts.join(', ')
  }
  if (value == null) return '-'
  return String(value)
}

const Session12ResponseBody = ({ data }) => (
  <div className="space-y-4 mt-3">
    {SECTIONS.map((section) => {
      const selected = data[section.id]
      const othersText = data[`${section.id}_othersText`]
      const display = formatSession12SectionValue(
        selected,
        selected?.includes(OTHERS_OPTION) ? othersText : ''
      )
      return (
        <Fragment key={section.id}>
          {section.groupTitle && (
            <div className="pt-2 pb-1 border-b border-purple-200 mb-2">
              <p className="text-sm font-bold text-gray-900">{section.groupTitle}</p>
            </div>
          )}
          <div className="border border-gray-100 rounded-lg p-3 bg-gray-50/80">
            <p className="text-xs font-semibold text-gray-700 mb-1">{section.title}</p>
            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{display}</p>
          </div>
        </Fragment>
      )
    })}
  </div>
)

const SessionFormResponses = () => {
  const { slug, sessionNumber } = useParams()
  const navigate = useNavigate()
  const [responses, setResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const sessionNum = Number(sessionNumber)
  const isSession12 = sessionNum === 12

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/login')
      return
    }
    fetchResponses()
  }, [slug, sessionNumber])

  const fetchResponses = async () => {
    try {
      const res = await api.get(`/api/programs/${slug}/facilitator/session-forms/${sessionNumber}`)
      if (res.data.success) {
        setResponses(res.data.data)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load form responses')
    } finally {
      setLoading(false)
    }
  }

  const fieldKeys = Object.keys(FORM_FIELD_LABELS)

  const pageTitle = isSession12
    ? `Session ${sessionNumber} — ${FORM_TITLE}`
    : `Session ${sessionNumber} - Health Form Responses`

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/" className="text-purple-600 hover:text-purple-800 font-medium">
            Go Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{pageTitle}</h1>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1 sm:ml-7">
            {responses.length} response{responses.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {responses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No form responses yet.</p>
          </div>
        ) : isSession12 ? (
          <div className="space-y-6">
            {responses.map((r) => {
              const user = r.userProgram?.user
              const data = r.formData || {}
              return (
                <div
                  key={r.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {(user?.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{user?.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 sm:text-right">{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <Session12ResponseBody data={data} />
                </div>
              )
            })}
          </div>
        ) : (
          <>
            <div className="space-y-4 md:hidden">
              {responses.map((r) => {
                const user = r.userProgram?.user
                const data = r.formData || {}
                return (
                  <div key={r.id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900">{user?.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="space-y-2">
                      {fieldKeys.map((key) => (
                        <div key={key} className="flex justify-between text-sm gap-2">
                          <span className="text-gray-500 shrink-0">{FORM_FIELD_LABELS[key]}</span>
                          <span className="text-gray-900 font-medium text-right max-w-[60%]">
                            {formatFormValue(data[key], key, data)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="hidden md:flex flex-col gap-4">
              {responses.map((r) => {
                const user = r.userProgram?.user
                const data = r.formData || {}
                const row1Keys = fieldKeys.slice(0, 5)
                const row2Keys = fieldKeys.slice(5)
                return (
                  <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm">
                          {(user?.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{user?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{user?.email || '-'}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="grid grid-cols-5 gap-3 mb-3">
                      {row1Keys.map((key) => (
                        <div key={key} className="min-w-0">
                          <p className="text-xs text-gray-500 mb-0.5">{FORM_FIELD_LABELS[key]}</p>
                          <p className="text-sm font-medium text-gray-900 break-words">{formatFormValue(data[key], key, data)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-5 gap-3">
                      {row2Keys.map((key) => (
                        <div key={key} className={`min-w-0 ${key === 'queries' ? 'col-span-2' : ''}`}>
                          <p className="text-xs text-gray-500 mb-0.5">{FORM_FIELD_LABELS[key]}</p>
                          <p className="text-sm font-medium text-gray-900 break-words">{formatFormValue(data[key], key, data)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SessionFormResponses
